import * as React from 'react'
import { KeyboardEvent, MouseEvent, ReactElement, useEffect, useMemo } from 'react'
import {
    css,
    FontSizes,
    getTheme,
    IContextualMenuItem,
    IPivotItemProps,
    Pivot,
    PivotItem,
    ResizeGroup,
} from '@fluentui/react'
import { appStyles } from '../../pages/App'
import { ViewFileProps } from '../../pages/File/ViewFile'
import { useHistory } from 'react-router-dom'
import useFilePages, { FilePageProps } from '../../hooks/file/useFilePages'
import { useFileModules } from '../../hooks/module/useFileModule'
import useModules from '../../hooks/module/useModules'
import useRoles, { rolesTasks, rolesTimes } from '../../hooks/useRoles'
import { ModuleType } from '../../context/GlobalContext'
import { useTranslation } from 'react-i18next'

type PivotSizeProps = { cacheKey: number }

export default function ViewFilePivotItems({ file, fileId }: ViewFileProps): ReactElement {
    const history = useHistory()
    const roles = useRoles()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const { pages, activePage, getPageRoute } = useFilePages(fileId)
    const fileModules = useFileModules(file.current.data)

    const getMenuItemOnClick = (_?: MouseEvent | KeyboardEvent, item?: IContextualMenuItem) =>
        history.push(getPageRoute(pages.find(({ key }: FilePageProps) => key === item?.key)?.route))

    const registrationItems = useMemo(
        () =>
            [
                modules.has(ModuleType.TimeTracking) &&
                    roles.any(rolesTimes) && {
                        key: 'times',
                        text: translate('file.pivot.registration.time'),
                        iconProps: { iconName: 'Timer' },
                        onClick: getMenuItemOnClick,
                    },
                modules.has(ModuleType.Invoices) && {
                    key: 'travels',
                    text: translate('file.pivot.registration.travel.label'),
                    iconProps: { iconName: 'Car' },
                    onClick: getMenuItemOnClick,
                },
                modules.has(ModuleType.Invoices) && {
                    key: 'disbursements',
                    text: translate('file.pivot.registration.disbursement.label'),
                    iconProps: { iconName: 'PaymentCard' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.Injury) && {
                    key: 'injury',
                    text: translate('file.pivot.registration.injury'),
                    iconProps: { iconName: 'Hospital' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.FamilyLaw) && {
                    key: 'personLaw',
                    text: translate('file.pivot.registration.personLaw'),
                    iconProps: { iconName: 'PeopleBlock' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.LegalAid) && {
                    key: 'legalAidCertificates',
                    text: translate('file.pivot.registration.legalAid'),
                    iconProps: { iconName: 'Money' },
                    onClick: getMenuItemOnClick,
                },
                fileModules.has(ModuleType.Docket) && {
                    key: 'docketProceedings',
                    text: translate('file.pivot.registration.docket'),
                    iconProps: { iconName: 'CompanyDirectory' },
                    onClick: getMenuItemOnClick,
                },
            ].filter(Boolean),
        [file.current.loaded, file.notes.data],
    )

    const pivotItems = useMemo(
        () =>
            [
                {
                    itemKey: 'parties',
                    itemIcon: 'Family',
                    headerText: translate('file.pivot.parties.title'),
                },
                {
                    itemKey: 'emails',
                    itemIcon: 'Mail',
                    headerText: translate('file.pivot.emails.title'),
                },
                {
                    itemKey: 'documents',
                    itemIcon: 'DocLibrary',
                    headerText: translate('file.pivot.documents.title'),
                },
                modules.has(ModuleType.Tasks) &&
                    roles.any(rolesTasks) && {
                        itemKey: 'tasks',
                        itemIcon: 'TaskLogo',
                        headerText: translate('file.pivot.tasks.title'),
                    },
                registrationItems.length > 0 && {
                    itemKey: 'registration',
                    itemIcon: 'BullseyeTargetEdit',
                    headerText: translate('file.pivot.registration.title'),
                    headerButtonProps: {
                        primary: true,
                        styles: { menuIcon: { color: 'inherit', marginRight: 0, fontSize: FontSizes.size10 } },
                        menuProps: {
                            items: registrationItems,
                        },
                    },
                },
                {
                    itemKey: 'notes',
                    itemIcon: 'DietPlanNotebook',
                    headerText: translate('file.pivot.notes.title'),
                    itemCount: file.notes?.data?.length || 0,
                },
            ].filter(Boolean),
        [file.current.loaded, file.notes.data],
    )

    useEffect(() => {
        fileModules.setCurrentFile(file.current.data)
    }, [file.current.loaded])

    return (
        <ResizeGroup
            className={css(appStyles.subMenu)}
            data={{ cacheKey: pivotItems.length }}
            onReduceData={(data: PivotSizeProps): PivotSizeProps | undefined =>
                data.cacheKey > 0 ? { cacheKey: data.cacheKey - 1 } : undefined
            }
            onGrowData={(data: PivotSizeProps): PivotSizeProps | undefined =>
                data.cacheKey < pivotItems.length ? { cacheKey: data.cacheKey + 1 } : undefined
            }
            onRenderData={(data: PivotSizeProps) => (
                <Pivot
                    linkSize={data.cacheKey === 1 ? 'large' : 'normal'}
                    linkFormat="tabs"
                    styles={{
                        count: { fontSize: FontSizes.size12 },
                        link: { ':after': { width: 0 }, ':active': { color: getTheme().palette.white } },
                    }}
                    selectedKey={activePage.pivotItemKey || activePage.key}
                    onLinkClick={item => {
                        history.push(getPageRoute(pages.find(page => page.key === item.props.itemKey)?.route))
                    }}
                >
                    {pivotItems.map((item: IPivotItemProps, i: number) => (
                        <PivotItem
                            key={item.itemKey}
                            {...item}
                            headerButtonProps={{ title: item.headerText, ...item.headerButtonProps }}
                            headerText={i <= data.cacheKey && data.cacheKey >= 2 ? item.headerText : undefined}
                        />
                    ))}
                </Pivot>
            )}
        />
    )
}
